import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import Input from "../Inputs/Input";
import Select from "../Inputs/Select";
import Toggle from "../shared/Toggle";
import {
  PROVIDER_AUTH_FORMAT_OPTIONS,
  RUNTIME_OIDC_TOKEN,
} from "../../utils/agentPolicyConnection";

const FIELD_LABEL_CLASS = "block text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 mb-1.5";
const HELP_TEXT_CLASS = "text-[11px] text-ink-gray-400 mt-1";
const SUB_HEADING_CLASS = "text-xs font-semibold text-ink-gray-900 dark:text-ink-white";

const CREDENTIAL_TYPES = [
  { value: "bearer", label: "Bearer Token", placeholder: "<bearer token>" },
  { value: "api_key", label: "API Key", placeholder: "<api key>" },
];

/**
 * Agent-side MCP authentication. Which fields apply depends on the selected
 * provider: a native provider carries no credentials of its own, so the agent
 * supplies one; every other provider signs the user in via OAuth 2.0 and only
 * the wire format is configurable.
 */
const AgentAuthSection = ({ values, onChange, disabled = false }) => {
  const [revealToken, setRevealToken] = useState(false);

  const isNative = values.service_type === "native";
  const runtimeIdentity = values.mcp_auth_token === RUNTIME_OIDC_TOKEN;
  const credentialType = values.auth_format === "bearer" ? "bearer" : "api_key";
  const credential = CREDENTIAL_TYPES.find((option) => option.value === credentialType);

  // A bearer token goes on Authorization; an API key needs its own header name.
  const selectCredentialType = (next) => onChange(
    next === "bearer"
      ? { auth_format: "bearer", auth_header: "" }
      : { auth_format: "raw" }
  );

  return (
    <div className="space-y-6">
      {values.service_type && (isNative ? (
        <div className="space-y-4">
          <div>
            <h4 className={SUB_HEADING_CLASS}>Agent Authentication</h4>
            <p className={HELP_TEXT_CLASS}>
              Used when the selected provider does not sign users in with OAuth 2.0 — such as the
              Photon provider. The agent authenticates to the MCP server with its own credential
              rather than the user's.
            </p>
          </div>

          {runtimeIdentity ? (
            <p className={HELP_TEXT_CLASS}>
              Runtime service identity is enabled, so the agent does not send a credential of its own.
            </p>
          ) : (
            <>
              <div>
                <span className={FIELD_LABEL_CLASS}>Credential type</span>
                <div className="flex gap-5">
                  {CREDENTIAL_TYPES.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-2 text-[13px] text-ink-ink dark:text-ink-white"
                    >
                      <input
                        type="radio"
                        name="agent_credential_type"
                        value={option.value}
                        checked={credentialType === option.value}
                        onChange={() => selectCredentialType(option.value)}
                        disabled={disabled}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className={FIELD_LABEL_CLASS}>{credential.label}</label>
                  <div className="relative">
                    <Input
                      type={revealToken ? "text" : "password"}
                      name="mcp_auth_token"
                      value={values.mcp_auth_token}
                      onChange={(event) => onChange({ mcp_auth_token: event.target.value })}
                      placeholder={credential.placeholder}
                      disabled={disabled}
                      className="pr-9"
                    />
                    <button
                      type="button"
                      onClick={() => setRevealToken((current) => !current)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-ink-gray-400"
                      aria-label={revealToken ? "Hide credential" : "Show credential"}
                    >
                      {revealToken ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <p className={HELP_TEXT_CLASS}>
                    Leave blank when the MCP server needs no credential
                  </p>
                </div>

                {credentialType === "api_key" && (
                  <div>
                    <label className={FIELD_LABEL_CLASS}>Header</label>
                    <Input
                      type="text"
                      name="auth_header"
                      value={values.auth_header}
                      onChange={(event) => onChange({ auth_header: event.target.value })}
                      placeholder="x-api-key"
                      disabled={disabled}
                    />
                    <p className={HELP_TEXT_CLASS}>Defaults to Authorization when blank</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <h4 className={SUB_HEADING_CLASS}>Provider Initiated Authentication</h4>
            <p className={HELP_TEXT_CLASS}>
              The selected provider signs each user in with OAuth 2.0. The agent calls the MCP
              server as that user, using their own credentials.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className={FIELD_LABEL_CLASS}>Credential transmission format</label>
              <Select
                value={values.auth_format}
                onChange={(value) => onChange({ auth_format: value })}
                options={PROVIDER_AUTH_FORMAT_OPTIONS}
                disabled={disabled}
              />
              <p className={HELP_TEXT_CLASS}>How the credential is sent to the MCP server</p>
            </div>
          </div>
        </div>
      ))}

      <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-4">
        <div className="flex items-center gap-3">
          <Toggle
            checked={runtimeIdentity}
            onChange={(next) => onChange({ mcp_auth_token: next ? RUNTIME_OIDC_TOKEN : "" })}
            disabled={disabled}
          />
          <span className={SUB_HEADING_CLASS}>Enable runtime service identity</span>
        </div>
        <p className={HELP_TEXT_CLASS}>
          For private MCP servers that sit behind infrastructure-level auth. Photon calls them with
          its own service identity instead of a user credential. Only for MCP servers Photon hosts
          itself. Requires Gluon Link.
        </p>
      </div>
    </div>
  );
};

export default AgentAuthSection;
